// Pricing calculation utilities for MusicSponsor

export interface PricingConfig {
  careerStage: string;
  baseCogs: number;
  volumeExponent: number;
  minCostSetCore: number;
  minCostSetAudio: number;
  minCostSetVideo: number;
}

export interface PackageConfig {
  type: 'SET Core' | 'SET Audio' | 'SET Video';
  cogsMultiplier?: number;
  markupMultiplier?: number;
}

export interface CalculationResult {
  basePrice: number;
  volumeDiscount: number;
  finalPrice: number;
  minCostApplied: boolean;
  formula: string;
}

// SET Core Pricing from database
export interface SetCorePricingData {
  careerStage: string;
  avgCapacity: number;
  price1Show: number;  // in cents
  price2Shows: number; // in cents per show
  price3Shows: number; // in cents per show
  price4Shows: number; // in cents per show
  price5PlusShows: number; // in cents per show
}

// SET Audio Pricing from database
export interface SetAudioPricingData {
  careerStage: string;
  avgCapacity: number;
  artistCogs: number;  // in cents
  exponent: string;    // stored as decimal string in DB
  packageType: string; // "SET Audio"
  packageMarkup: string; // stored as decimal string in DB
  minCostPerShow: number; // in cents
}

// SET Video Pricing from database
export interface SetVideoPricingData {
  careerStage: string;
  avgCapacity: number;
  artistCogs: number;  // in cents
  exponent: string;    // stored as decimal string in DB
  packageType: string; // "SET Video"
  packageMarkup: string; // stored as decimal string in DB (2.875)
  minCostPerShow: number; // in cents
}

// Default pricing configurations
export const defaultPricingConfigs: PricingConfig[] = [
  {
    careerStage: 'Developing',
    baseCogs: 882.00,  // Half of Emerging
    volumeExponent: 0.40,  // Lower than Emerging
    minCostSetCore: 449.00,
    minCostSetAudio: 674.00,
    minCostSetVideo: 899.00,
  },
  {
    careerStage: 'Emerging',
    baseCogs: 1764.00,
    volumeExponent: 0.443151,
    minCostSetCore: 999.00,
    minCostSetAudio: 1249.00,
    minCostSetVideo: 1499.00,
  },
  {
    careerStage: 'Established 1',
    baseCogs: 3566.00,
    volumeExponent: 0.536866,
    minCostSetCore: 1499.00,
    minCostSetAudio: 1999.00,
    minCostSetVideo: 2499.00,
  },
  {
    careerStage: 'Established 2',
    baseCogs: 4911.00,
    volumeExponent: 0.579442,
    minCostSetCore: 1749.00,
    minCostSetAudio: 2499.00,
    minCostSetVideo: 3999.00,
  },
  {
    careerStage: 'Established 3',
    baseCogs: 6763.00,
    volumeExponent: 0.622001,
    minCostSetCore: 1999.00,
    minCostSetAudio: 2999.00,
    minCostSetVideo: 5999.00,
  },
  {
    careerStage: 'Mainstream 1',
    baseCogs: 9314.00,
    volumeExponent: 0.664545,
    minCostSetCore: 2499.00,
    minCostSetAudio: 4999.00,
    minCostSetVideo: 9999.00,
  },
  {
    careerStage: 'Mainstream 2',
    baseCogs: 14578.00,
    volumeExponent: 0.724129,
    minCostSetCore: 3999.00,
    minCostSetAudio: 7499.00,
    minCostSetVideo: 14999.00,
  },
  {
    careerStage: 'Mainstream 3',
    baseCogs: 22818.00,
    volumeExponent: 0.783698,
    minCostSetCore: 4999.00,
    minCostSetAudio: 14999.00,
    minCostSetVideo: 29999.00,
  },
  {
    careerStage: 'Mainstream 4',
    baseCogs: 33499.00,
    volumeExponent: 0.8347845,
    minCostSetCore: 7499.00,
    minCostSetAudio: 24999.00,
    minCostSetVideo: 49999.00,
  },
];

// Default package configurations
export const defaultPackageConfigs: PackageConfig[] = [
  {
    type: 'SET Core',
    cogsMultiplier: 0.3,
  },
  {
    type: 'SET Audio',
    markupMultiplier: 1.5,
  },
  {
    type: 'SET Video',
    markupMultiplier: 2.875,
  },
];

/**
 * Calculate pricing for a sponsorship package
 * 
 * Rules:
 * - 1 show: Base COGS × Package Markup
 * - 2+ shows: COGS × (# shows ^ career stage exponent), enforced by minimum cost per show
 */
export function calculatePackagePrice(
  careerStage: string,
  packageType: 'SET Core' | 'SET Audio' | 'SET Video',
  numberOfShows: number,
  customPricingConfig?: PricingConfig,
  customPackageConfig?: PackageConfig
): CalculationResult {
  // Get pricing configuration
  const pricingConfig = customPricingConfig || 
    defaultPricingConfigs.find(c => c.careerStage === careerStage);
  
  if (!pricingConfig) {
    throw new Error(`No pricing configuration found for career stage: ${careerStage}`);
  }

  // Get package configuration
  const packageConfig = customPackageConfig || 
    defaultPackageConfigs.find(c => c.type === packageType);
  
  if (!packageConfig) {
    throw new Error(`No package configuration found for type: ${packageType}`);
  }

  const { baseCogs, volumeExponent } = pricingConfig;

  // Get minimum cost for this package type
  const minCost = packageType === 'SET Core' ? pricingConfig.minCostSetCore :
                  packageType === 'SET Audio' ? pricingConfig.minCostSetAudio :
                  pricingConfig.minCostSetVideo;

  if (numberOfShows === 1) {
    // Single show: COGS × Package Markup
    let finalPrice: number;
    let formula: string;
    
    if (packageConfig.cogsMultiplier) {
      // GA package: COGS × cogsMultiplier
      finalPrice = baseCogs * packageConfig.cogsMultiplier;
      formula = `${baseCogs} × ${packageConfig.cogsMultiplier} = $${finalPrice.toFixed(2)}`;
    } else {
      // VIP/All-Access: COGS × markupMultiplier
      finalPrice = baseCogs * packageConfig.markupMultiplier!;
      formula = `${baseCogs} × ${packageConfig.markupMultiplier} = $${finalPrice.toFixed(2)}`;
    }
    
    const minCostApplied = finalPrice < minCost;
    const result = Math.max(finalPrice, minCost);

    return {
      basePrice: finalPrice,
      volumeDiscount: 0,
      finalPrice: result,
      minCostApplied,
      formula: formula + (minCostApplied ? ` (min: $${minCost})` : '')
    };
  } else {
    // Multiple shows: (COGS × (# shows ^ exponent)) × Package Markup
    const volumeMultiplier = Math.pow(numberOfShows, volumeExponent);
    const volumePrice = baseCogs * volumeMultiplier;
    
    let finalVolumePrice: number;
    let formula: string;
    
    if (packageConfig.cogsMultiplier) {
      // GA package: (COGS × (# shows ^ exponent)) × cogsMultiplier
      finalVolumePrice = volumePrice * packageConfig.cogsMultiplier;
      formula = `(${baseCogs} × ${numberOfShows}^${volumeExponent}) × ${packageConfig.cogsMultiplier} = $${finalVolumePrice.toFixed(2)}`;
    } else {
      // VIP/All-Access: (COGS × (# shows ^ exponent)) × markupMultiplier
      finalVolumePrice = volumePrice * packageConfig.markupMultiplier!;
      formula = `(${baseCogs} × ${numberOfShows}^${volumeExponent}) × ${packageConfig.markupMultiplier} = $${finalVolumePrice.toFixed(2)}`;
    }
    
    // Enforce minimum cost per show
    const totalMinCost = minCost * numberOfShows;
    const minCostApplied = finalVolumePrice < totalMinCost;
    const result = Math.max(finalVolumePrice, totalMinCost);

    return {
      basePrice: volumePrice,
      volumeDiscount: volumeMultiplier,
      finalPrice: result,
      minCostApplied,
      formula: formula + (minCostApplied ? ` (min: $${totalMinCost})` : '')
    };
  }
}

/**
 * Calculate pricing for all package types for a given career stage and show count
 */
export function calculateAllPackages(
  careerStage: string,
  numberOfShows: number,
  customPricingConfig?: PricingConfig
): Record<string, CalculationResult> {
  const packages: ('SET Core' | 'SET Audio' | 'SET Video')[] = ['SET Core', 'SET Audio', 'SET Video'];

  return packages.reduce((acc, packageType) => {
    acc[packageType] = calculatePackagePrice(
      careerStage,
      packageType,
      numberOfShows,
      customPricingConfig
    );
    return acc;
  }, {} as Record<string, CalculationResult>);
}

/**
 * Calculate SET Core pricing using fixed table pricing from database
 * This replaces the volume exponent formula for SET Core packages
 */
export function calculateSetCorePriceFromTable(
  setCorePricingData: SetCorePricingData,
  numberOfShows: number
): CalculationResult {
  let pricePerShow: number;
  let totalPrice: number;

  // Determine price per show based on number of shows
  if (numberOfShows === 1) {
    pricePerShow = setCorePricingData.price1Show;
    totalPrice = pricePerShow;
  } else if (numberOfShows === 2) {
    pricePerShow = setCorePricingData.price2Shows;
    totalPrice = pricePerShow * 2;
  } else if (numberOfShows === 3) {
    pricePerShow = setCorePricingData.price3Shows;
    totalPrice = pricePerShow * 3;
  } else if (numberOfShows === 4) {
    pricePerShow = setCorePricingData.price4Shows;
    totalPrice = pricePerShow * 4;
  } else {
    // 5+ shows
    pricePerShow = setCorePricingData.price5PlusShows;
    totalPrice = pricePerShow * numberOfShows;
  }

  // Convert from cents to dollars
  const finalPrice = totalPrice / 100;
  const pricePerShowDollars = pricePerShow / 100;

  return {
    basePrice: finalPrice,
    volumeDiscount: 0,
    finalPrice: finalPrice,
    minCostApplied: false,
    formula: numberOfShows === 1
      ? `$${pricePerShowDollars.toFixed(2)} for 1 show`
      : `$${pricePerShowDollars.toFixed(2)} × ${numberOfShows} shows = $${finalPrice.toFixed(2)}`
  };
}

/**
 * Calculate SET Audio pricing using formula with database values
 * This uses the sublinear curve formula: (COGS × (# shows ^ exponent)) × Package Markup
 */
export function calculateSetAudioPriceFromTable(
  setAudioPricingData: SetAudioPricingData,
  numberOfShows: number
): CalculationResult {
  // Convert database values from cents to dollars and strings to numbers
  const artistCogs = setAudioPricingData.artistCogs / 100; // Convert cents to dollars
  const exponent = parseFloat(setAudioPricingData.exponent);
  const packageMarkup = parseFloat(setAudioPricingData.packageMarkup);
  const minCostPerShow = setAudioPricingData.minCostPerShow / 100; // Convert cents to dollars

  if (numberOfShows === 1) {
    // Single show: COGS × Package Markup
    const finalPrice = artistCogs * packageMarkup;
    const minCostApplied = finalPrice < minCostPerShow;
    const result = Math.max(finalPrice, minCostPerShow);

    return {
      basePrice: finalPrice,
      volumeDiscount: 0,
      finalPrice: result,
      minCostApplied,
      formula: `$${artistCogs.toFixed(2)} × ${packageMarkup} = $${finalPrice.toFixed(2)}` +
               (minCostApplied ? ` (min: $${minCostPerShow})` : '')
    };
  } else {
    // Multiple shows: (COGS × (# shows ^ exponent)) × Package Markup
    const volumeMultiplier = Math.pow(numberOfShows, exponent);
    const volumePrice = artistCogs * volumeMultiplier;
    const finalVolumePrice = volumePrice * packageMarkup;

    // Enforce minimum cost per show
    const totalMinCost = minCostPerShow * numberOfShows;
    const minCostApplied = finalVolumePrice < totalMinCost;
    const result = Math.max(finalVolumePrice, totalMinCost);

    return {
      basePrice: volumePrice,
      volumeDiscount: volumeMultiplier,
      finalPrice: result,
      minCostApplied,
      formula: `($${artistCogs.toFixed(2)} × ${numberOfShows}^${exponent}) × ${packageMarkup} = $${finalVolumePrice.toFixed(2)}` +
               (minCostApplied ? ` (min: $${totalMinCost})` : '')
    };
  }
}

/**
 * Calculate SET Video pricing using formula with database values
 * This uses the sublinear curve formula: (COGS × (# shows ^ exponent)) × Package Markup (2.875)
 */
export function calculateSetVideoPriceFromTable(
  setVideoPricingData: SetVideoPricingData,
  numberOfShows: number
): CalculationResult {
  // Convert database values from cents to dollars and strings to numbers
  const artistCogs = setVideoPricingData.artistCogs / 100; // Convert cents to dollars
  const exponent = parseFloat(setVideoPricingData.exponent);
  const packageMarkup = parseFloat(setVideoPricingData.packageMarkup);
  const minCostPerShow = setVideoPricingData.minCostPerShow / 100; // Convert cents to dollars

  if (numberOfShows === 1) {
    // Single show: COGS × Package Markup
    const finalPrice = artistCogs * packageMarkup;
    const minCostApplied = finalPrice < minCostPerShow;
    const result = Math.max(finalPrice, minCostPerShow);

    return {
      basePrice: finalPrice,
      volumeDiscount: 0,
      finalPrice: result,
      minCostApplied,
      formula: `$${artistCogs.toFixed(2)} × ${packageMarkup} = $${finalPrice.toFixed(2)}` +
               (minCostApplied ? ` (min: $${minCostPerShow})` : '')
    };
  } else {
    // Multiple shows: (COGS × (# shows ^ exponent)) × Package Markup
    const volumeMultiplier = Math.pow(numberOfShows, exponent);
    const volumePrice = artistCogs * volumeMultiplier;
    const finalVolumePrice = volumePrice * packageMarkup;

    // Enforce minimum cost per show
    const totalMinCost = minCostPerShow * numberOfShows;
    const minCostApplied = finalVolumePrice < totalMinCost;
    const result = Math.max(finalVolumePrice, totalMinCost);

    return {
      basePrice: volumePrice,
      volumeDiscount: volumeMultiplier,
      finalPrice: result,
      minCostApplied,
      formula: `($${artistCogs.toFixed(2)} × ${numberOfShows}^${exponent}) × ${packageMarkup} = $${finalVolumePrice.toFixed(2)}` +
               (minCostApplied ? ` (min: $${totalMinCost})` : '')
    };
  }
}

/**
 * Calculate all packages with database pricing
 * Use this when pricing data is available from the database
 */
export function calculateAllPackagesWithDatabasePricing(
  careerStage: string,
  numberOfShows: number,
  setCorePricingData: SetCorePricingData | null,
  customPricingConfig?: PricingConfig,
  setAudioPricingData?: SetAudioPricingData | null,
  setVideoPricingData?: SetVideoPricingData | null
): Record<string, CalculationResult> {
  const results: Record<string, CalculationResult> = {};

  // Calculate SET Core price from database if available, otherwise use formula
  if (setCorePricingData) {
    results['SET Core'] = calculateSetCorePriceFromTable(setCorePricingData, numberOfShows);
  } else {
    results['SET Core'] = calculatePackagePrice(careerStage, 'SET Core', numberOfShows, customPricingConfig);
  }

  // Calculate SET Audio price from database if available, otherwise use formula
  if (setAudioPricingData) {
    results['SET Audio'] = calculateSetAudioPriceFromTable(setAudioPricingData, numberOfShows);
  } else {
    results['SET Audio'] = calculatePackagePrice(careerStage, 'SET Audio', numberOfShows, customPricingConfig);
  }

  // Calculate SET Video price from database if available, otherwise use formula
  if (setVideoPricingData) {
    results['SET Video'] = calculateSetVideoPriceFromTable(setVideoPricingData, numberOfShows);
  } else {
    results['SET Video'] = calculatePackagePrice(careerStage, 'SET Video', numberOfShows, customPricingConfig);
  }

  return results;
}